// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Combobox, Icon, Input } from '@node-space/ui-components'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { useRoutingNumberValidation } from 'hooks/queries/Beneficiaries/useRoutingNumberValidation'
import { useBicValidation } from 'hooks/queries/useBicValidation'
import { useFilteredCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useIbanValidation } from 'hooks/queries/useIbanValidation'
import { useAddBeneficiaryContext } from 'hooks/useAddBeneficiaryContext'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import {
  AddBeneficiaryBankDetailsPayload,
  AddBeneficiaryBankDetailsPayloadKeys,
  BeneficiaryDetailsCurrency,
  ValidateIbanResponse,
  ValidateRoutingNumberResponse,
} from 'types/beneficiaries'
import {
  getInternationalAccountNumberPlaceholder,
  getInternationalBankCodePlaceholder,
  isIbanFormat,
  setBankFieldsFromRoutingNumberValidation,
  setFieldsFetchedFromIbanValidation,
} from 'utils/beneficiaries'
import { IBAN_VALIDATION_ERRORS } from '../../../constants'
import { addBeneficiaryBankDetailsSchema } from '../schemas/addBeneficiaryBankDetailsSchema'

const fieldNames: AddBeneficiaryBankDetailsPayloadKeys[] = [
  'bicSwiftCode',
  'bankName',
  'bankAddressLine1',
  'bankAddressLine2',
  'bankCity',
  'bankPostCode',
  'bankRegion',
  'bankCountry',
]

interface BeneficiaryBankDetailsProps {
  onBack: () => void
  onContinue: (bankDetails: AddBeneficiaryBankDetailsPayload) => void
  isSubmitting: boolean
}

export const BeneficiaryBankDetails = ({
  onBack,
  onContinue,
  isSubmitting,
}: BeneficiaryBankDetailsProps) => {
  const { t } = useTranslation()
  const { data: countriesData, isPending: countriesLoading } = useFilteredCountriesQuery(true)
  const countries = countriesData.map(country => ({
    leftText: country?.name,
    value: country?.code,
  }))
  const {
    beneficiaryFormState,
    beneficiaryFormErrors,
    setBeneficiaryFormErrors,
    beneficiaryIbanValidationState,
    setBeneficiaryIbanValidationState,
    beneficiaryBicValidationState,
    setBeneficiaryBicValidationState,
  } = useAddBeneficiaryContext()
  const { setRequestError } = useMappedErrorMessage()
  const formSchema = addBeneficiaryBankDetailsSchema(t)
  const { register, formState, setValue, control, trigger, clearErrors, watch, setError } =
    useForm<AddBeneficiaryBankDetailsPayload>({
      resolver: yupFormResolver<AddBeneficiaryBankDetailsPayload>(formSchema),
    })
  const [ibanValidationData, setIbanValidationData] = useState<AddBeneficiaryBankDetailsPayload>(
    beneficiaryIbanValidationState
  )

  const [
    intlIban,
    bicSwiftCode,
    routingNumber,
    bankName,
    bankAddressLine1,
    bankAddressLine2,
    bankCity,
    bankPostCode,
    bankRegion,
    bankCountry,
  ] = useWatch({
    control,
    name: [
      'intlIban',
      'bicSwiftCode',
      'routingNumber',
      'bankName',
      'bankAddressLine1',
      'bankAddressLine2',
      'bankCity',
      'bankPostCode',
      'bankRegion',
      'bankCountry',
    ],
    defaultValue: {
      intlIban: '',
      bicSwiftCode: '',
      routingNumber: '',
      bankName: '',
      bankAddressLine1: '',
      bankAddressLine2: '',
      bankCity: '',
      bankPostCode: '',
      bankRegion: '',
      bankCountry: '',
    },
  })

  const {
    isFetching: isValidatingIban,
    refetch: validateIban,
    isError: isValidatingIbanError,
  } = useIbanValidation(intlIban, 'IBAN', setRequestError, false)

  const {
    data: bicValidation,
    refetch: validateBic,
    isFetching: isValidatingBic,
  } = useBicValidation(bicSwiftCode, setRequestError, false)

  const {
    data: usBankDetails,
    isFetching: isValidatingRoutingNumber,
    refetch: validateRoutingNumber,
  } = useRoutingNumberValidation(routingNumber, setRequestError, false)

  const formValues = watch()

  const currencyCode = beneficiaryFormState?.currencyCode
  const isUSD = currencyCode === BeneficiaryDetailsCurrency.USD
  const isUSDAndUS = isUSD && formValues?.bankCountry === 'US'
  const isIban = isIbanFormat(intlIban)

  useEffect(() => {
    setCachedValues()
    setCachedIbanValidationValues()
  }, [])

  useEffect(() => {
    if (beneficiaryFormErrors) {
      Object.keys(beneficiaryFormErrors).forEach((field: AddBeneficiaryBankDetailsPayloadKeys) => {
        setError(field, beneficiaryFormErrors[field])
      })
    } else {
      clearErrors()
    }
  }, [setError, clearErrors, beneficiaryFormErrors])

  const setCachedValues = () => {
    Object.keys(beneficiaryFormState || {}).forEach(
      (field: keyof AddBeneficiaryBankDetailsPayload) => {
        if (beneficiaryFormState?.[field]) {
          setValue(field, beneficiaryFormState?.[field])
        }
      }
    )
  }

  const setCachedIbanValidationValues = () => {
    Object.keys(beneficiaryIbanValidationState || {}).forEach(
      (field: keyof AddBeneficiaryBankDetailsPayload) => {
        if (beneficiaryIbanValidationState?.[field]) {
          setValue(field, beneficiaryIbanValidationState?.[field])
        }
      }
    )
  }

  const countryOptions = useMemo(() => {
    if (countries?.length) {
      return countries?.map(country => ({
        leftText: country?.leftText,
        value: country?.value,
      }))
    }

    return []
  }, [countries])

  const handleChange = (field: keyof AddBeneficiaryBankDetailsPayload, value: string) => {
    setValue(field, value)
  }

  const setApiData = (details: AddBeneficiaryBankDetailsPayload) => {
    setIbanValidationData(details)
    setBeneficiaryIbanValidationState(details)

    if (Object.keys(details || {})?.length) {
      Object.keys(details)?.forEach(detail => {
        if (detail === 'bankCountry') {
          handleCountrySelect(details[detail])
        } else {
          handleChange(detail as keyof AddBeneficiaryBankDetailsPayload, details[detail])
        }
      })
    } else {
      setIbanValidationData(null)
    }
  }

  const setBankDetailsFromApi = (bankDetails: ValidateIbanResponse) => {
    const details = setFieldsFetchedFromIbanValidation(bankDetails)
    setApiData(details)
  }

  const setBankDetailsFromRoutingNumberApi = (
    bankDetailsResponse: ValidateRoutingNumberResponse
  ) => {
    const details = setBankFieldsFromRoutingNumberValidation(bankDetailsResponse)
    setApiData(details)
  }

  const handleInternationalIbanOnBlur = async () => {
    if (isIban) {
      if (beneficiaryIbanValidationState) {
        setIbanValidationData(null)
        setBeneficiaryIbanValidationState(null)
        fieldNames.forEach(fieldName => {
          setValue(fieldName as keyof AddBeneficiaryBankDetailsPayload, '')
        })
      }
      clearErrors()

      const isFormFieldValid = await trigger('intlIban')
      const ibanAccountDetails = isFormFieldValid && !!intlIban && (await validateIban())
      const accountDetails = ibanAccountDetails?.data
      const isValidIban =
        (!ibanAccountDetails && ibanValidationData && !beneficiaryIbanValidationState) ||
        isValidatingIbanError

      if (ibanAccountDetails?.isError) {
        setBeneficiaryFormErrors({
          ...beneficiaryFormErrors,
          intlIban: { message: t('ibanValidationError') },
        })
        return
      }

      if (isValidIban) {
        Object.keys(ibanValidationData || {})?.forEach(detail => {
          if (detail === 'bankCountry') {
            handleCountrySelect('')
          } else {
            handleChange(detail as keyof AddBeneficiaryBankDetailsPayload, '')
          }
        })
        setIbanValidationData(null)
      } else if (!accountDetails?.validationFailed?.length) {
        setBankDetailsFromApi(accountDetails)
      } else if (accountDetails?.validationFailed?.length >= 1) {
        const countryDoesNotSupportIban = accountDetails.validationFailed.includes(
          IBAN_VALIDATION_ERRORS.COUNTRY_DOES_NOT_SUPPORT_IBAN
        )

        if (!countryDoesNotSupportIban) {
          setBeneficiaryFormErrors({
            ...beneficiaryFormErrors,
            intlIban: { message: t('invalidAccountNumberErrorMessage') },
          })
        }
      }
    }
  }

  const handleInternationalBicOnBlur = async () => {
    if (beneficiaryBicValidationState) {
      setBeneficiaryBicValidationState(null)
    }
    clearErrors()
    if (!isIban && bicSwiftCode) {
      const bicValidationResponse = await validateBic()
      if (bicValidationResponse && bicValidationResponse?.data !== 'Valid') {
        setBeneficiaryFormErrors({
          ...beneficiaryFormErrors,
          bicSwiftCode: { message: t('invalidBicCodeErrorMessage') },
        })
      } else {
        setBeneficiaryBicValidationState(bicValidationResponse?.data)
      }
    }
  }

  const handleOnRoutingNumberBlur = async () => {
    if (routingNumber) {
      if (beneficiaryIbanValidationState) {
        setIbanValidationData(null)
        setBeneficiaryIbanValidationState(null)
        fieldNames.forEach(fieldName => {
          if (fieldName !== 'bankCountry') {
            setValue(fieldName, '')
          }
        })
      }
      clearErrors()

      const isFormFieldValid = await trigger('routingNumber')
      const response = isFormFieldValid && (await validateRoutingNumber())
      const bankDetails = response?.data
      const isValidRoutingNumber =
        !!bankDetails?.bankDetails?.bankName && !bankDetails?.validationFailed?.length

      if (response?.isError) {
        setBeneficiaryFormErrors({
          ...beneficiaryFormErrors,
          routingNumber: { message: t('invalidRoutingNumberErrorMessage') },
        })

        return
      }

      if (isValidRoutingNumber) {
        const { bankAddress, bankName } = bankDetails?.bankDetails || {}
        const { addressLine1, addressLine2, city, region, postCode, countryCode } =
          bankAddress || {}
        handleChange('bankName', bankName)
        handleChange('bankAddressLine1', addressLine1)
        handleChange('bankAddressLine2', addressLine2)
        handleChange('bankCity', city)
        handleChange('bankRegion', region)
        handleChange('bankPostCode', postCode)
        handleChange('bankCountry', countryCode)
        setBankDetailsFromRoutingNumberApi(bankDetails)
      } else if (bankDetails?.validationFailed?.length) {
        setIbanValidationData(null)
        setBeneficiaryFormErrors({
          ...beneficiaryFormErrors,
          routingNumber: { message: t('invalidRoutingNumberErrorMessage') },
        })
      }
    }
  }

  const isBicValid = bicValidation === 'Valid'

  const submitBankAddressAccountDetails = async () => {
    const validateFields: AddBeneficiaryBankDetailsPayloadKeys[] = [
      'intlIban',
      'bicSwiftCode',
      'bankName',
      'bankAddressLine1',
      'bankAddressLine2',
      'bankCity',
      'bankPostCode',
      'bankRegion',
      'bankCountry',
    ]

    const isIbanInvalid =
      formState?.errors?.intlIban?.message === t('invalidAccountNumberErrorMessage')
    const isRoutingNumberInvalid =
      currencyCode === BeneficiaryDetailsCurrency.USD &&
      bankCountry === 'US' &&
      formState?.errors?.routingNumber?.message === t('invalidRoutingNumberErrorMessage')

    if (isIbanInvalid || isRoutingNumberInvalid) {
      clearErrors(fieldNames as AddBeneficiaryBankDetailsPayloadKeys[])

      await trigger(validateFields)
    } else {
      clearErrors()
      if (currencyCode === BeneficiaryDetailsCurrency.USD && bankCountry === 'US') {
        validateFields.push('routingNumber')
      } else {
        validateFields.push('intlIban')
      }

      const verifyFields = await trigger(validateFields)

      if (verifyFields) {
        return onContinue(formValues)
      }
    }
  }

  const handleCountrySelect = (countryCode: string) => {
    const countryName = countryOptions.find(country => country?.value === countryCode)?.leftText
    setValue('bankCountryName', countryName)
    setValue('bankCountry', countryCode)
  }

  const isIbanValid = !!ibanValidationData?.intlIban
  const isBicSwiftCodeValid =
    (isIban && !!ibanValidationData?.bicSwiftCode) || (!isIban && isBicValid)
  const isValidRoutingNumber =
    !!usBankDetails?.bankDetails?.bankAddress &&
    !usBankDetails?.validationFailed.length &&
    !!routingNumber

  return (
    <Box padding={24} flex flexCol width="full">
      <Box flexCol width="full" paddingB={16}>
        {isUSD && (
          <Box flexSize="fill" paddingB={16}>
            <Combobox
              key={bankCountry}
              {...register('bankCountry')}
              label={t('country')}
              id="bank-country"
              selectPlaceholder={t('searchOrSelectACountry')}
              items={countries}
              onChange={handleCountrySelect}
              loading={countriesLoading || isValidatingIban}
              defaultValue={bankCountry}
              errorText={formState?.errors?.bankCountry?.message}
            />
          </Box>
        )}

        <Input
          {...register('intlIban')}
          label={t('formFields.accountNumberOrIban')}
          inputMode="text"
          name="intlIban"
          test-id="input-intl-iban"
          errorText={formState?.errors?.intlIban?.message}
          error={!!formState?.errors?.intlIban}
          value={intlIban}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleChange('intlIban', event?.target?.value)
          }
          onBlur={handleInternationalIbanOnBlur}
          IconButton={
            isIban && isIbanValid ? (
              <Button variant="ghost" iconElement={<Icon name="Check" />} />
            ) : null
          }
          iconPosition="right"
          placeholder={getInternationalAccountNumberPlaceholder(currencyCode, t)}
          loading={isValidatingIban}
        />
      </Box>

      {isUSDAndUS && (
        <Box flexCol width="full" paddingB={16}>
          <Input
            {...register('routingNumber')}
            label={t('abaRoutingNumber')}
            placeholder={t('abaRoutingNumberPlaceholder')}
            inputMode="text"
            name="routingNumber"
            testId="input-routing-number"
            errorText={formState?.errors?.routingNumber?.message}
            error={!!formState?.errors?.routingNumber}
            value={routingNumber}
            onChange={event => handleChange('routingNumber', event?.target?.value)}
            onBlur={handleOnRoutingNumberBlur}
            IconButton={
              isValidRoutingNumber && !isValidatingRoutingNumber ? (
                <Button variant="ghost" iconElement={<Icon name="Check" />} />
              ) : null
            }
            iconPosition="right"
            loading={isValidatingIban || isValidatingRoutingNumber}
          />
        </Box>
      )}

      <Box flexCol width="full" paddingB={16}>
        <Input
          {...register('bicSwiftCode')}
          label={t('formFields.swiftBankCode')}
          inputMode="text"
          name="bicSwiftCode"
          test-id="input-bic-swift"
          errorText={formState?.errors?.bicSwiftCode?.message}
          error={!!formState?.errors?.bicSwiftCode}
          value={bicSwiftCode}
          onChange={event => handleChange('bicSwiftCode', event?.target?.value)}
          placeholder={getInternationalBankCodePlaceholder(currencyCode, t)}
          IconButton={
            isBicSwiftCodeValid ? (
              <Button variant="ghost" iconElement={<Icon name="Check" />} />
            ) : null
          }
          iconPosition="right"
          onBlur={handleInternationalBicOnBlur}
          loading={isValidatingIban || isValidatingBic}
        />
      </Box>
      <Box flexCol width="full" flex paddingB={16}>
        <Input
          {...register('bankName')}
          label={t('beneficiarysBankName')}
          inputMode="text"
          name="bankName"
          test-id="input-bank-name"
          errorText={formState?.errors?.bankName?.message}
          error={!!formState?.errors?.bankName}
          value={bankName}
          onChange={event => handleChange('bankName', event?.target?.value)}
          placeholder={t('enterBankName')}
          loading={isValidatingIban || isValidatingRoutingNumber}
        />
      </Box>

      <Box flexCol width="full" flex paddingB={16}>
        <Input
          {...register('bankAddressLine1')}
          label={t('bankAddressLine1')}
          inputMode="text"
          name="bankAddressLine1"
          test-id="input-bank-address-line-1"
          errorText={formState?.errors?.bankAddressLine1?.message}
          error={!!formState?.errors?.bankAddressLine1}
          value={bankAddressLine1}
          onChange={event => handleChange('bankAddressLine1', event?.target?.value)}
          placeholder={t('inputPlaceholders.enterAddressLine1Placeholder')}
          loading={isValidatingIban || isValidatingRoutingNumber}
        />
      </Box>
      <Box flexCol width="full" flex paddingB={16}>
        <Input
          {...register('bankAddressLine2')}
          label={t('bankAddressLine2')}
          inputMode="text"
          name="bankAddressLine2"
          test-id="input-bank-address-line-2"
          errorText={formState?.errors?.bankAddressLine2?.message}
          error={!!formState?.errors?.bankAddressLine2}
          value={bankAddressLine2}
          onChange={event => handleChange('bankAddressLine2', event?.target?.value)}
          placeholder={t('inputPlaceholders.enterAddressLine2Placeholder')}
          loading={isValidatingIban || isValidatingRoutingNumber}
        />
      </Box>
      <Box flex gapX={12} paddingB={16}>
        <Box flexSize="auto" width="full">
          <Input
            {...register('bankCity')}
            label={t('city')}
            inputMode="text"
            name="city"
            test-id="input-bank-city"
            errorText={formState?.errors?.bankCity?.message}
            error={!!formState?.errors?.bankCity}
            value={bankCity}
            onChange={event => handleChange('bankCity', event?.target?.value)}
            placeholder={t('inputPlaceholders.enterCityPlaceholder')}
            loading={isValidatingIban || isValidatingRoutingNumber}
          />
        </Box>
        <Box flexSize="auto" width="full">
          <Input
            {...register('bankPostCode')}
            label={t('postCode')}
            inputMode="text"
            name="bic"
            test-id="input-bank-post-code"
            errorText={formState?.errors?.bankPostCode?.message}
            error={!!formState?.errors?.bankPostCode}
            value={bankPostCode}
            onChange={event => handleChange('bankPostCode', event?.target?.value)}
            placeholder={t('inputPlaceholders.enterPostalCodePlaceholder')}
            loading={isValidatingIban || isValidatingRoutingNumber}
          />
        </Box>
      </Box>
      <Box flexCol width="full" gapX={12} paddingB={16}>
        <Input
          {...register('bankRegion')}
          label={t('region')}
          inputMode="text"
          name="bankRegion"
          test-id="input-bank-region"
          errorText={formState?.errors?.bankRegion?.message}
          error={!!formState?.errors?.bankRegion}
          value={bankRegion}
          onChange={event => handleChange('bankRegion', event?.target?.value)}
          placeholder={t('inputPlaceholders.enterRegionPlaceholder')}
          loading={isValidatingIban || isValidatingRoutingNumber}
        />
      </Box>
      {!isUSD && (
        <Box flexSize="fill">
          <Combobox
            {...register('bankCountry')}
            label={t('country')}
            id="bank-country"
            selectPlaceholder={t('searchOrSelectACountry')}
            items={countries}
            onChange={handleCountrySelect}
            loading={countriesLoading || isValidatingIban}
            defaultValue={bankCountry}
            errorText={formState?.errors?.bankCountry?.message}
          />
        </Box>
      )}

      <Box flex justify="between" paddingT={24} testId="back">
        <Button
          onClick={() => {
            clearErrors()
            onBack()
          }}
          variant="secondary"
        >
          {t('back')}
        </Button>
        <Button
          testId="continue"
          onClick={submitBankAddressAccountDetails}
          loading={isSubmitting || isValidatingIban}
        >
          {t('continue')}
        </Button>
      </Box>
    </Box>
  )
}
